import React, { useRef, useCallback } from 'react';
import { FileInput, ImageField, useNotify, useTranslate } from 'react-admin';
import { Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import ExportIcon from '@/assets/icons/export-icon.svg?react';
import ImportLoadingIcon from '@/assets/icons/loading-icon.svg?react';
import { styled } from '@mui/material/styles';
import { importInputBaseStyles } from '@/themes/commonStyles';
import { useWatch, useFormContext } from 'react-hook-form';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useImagePreview } from '@/composables/useImagePreview';
import { ContractorLogoField } from '@/components/contractors/ContractorLogoField';

const StyledContractorLogoInput = styled(Box)({
    ...importInputBaseStyles,
    '& .RaLabeled-label': {
        display: 'none',
    },
    '& .RaFileInput-dropZone.RaFileInput-dropZone': {
        position: 'relative',
        padding: '32px',
        minHeight: 'auto',
        borderRadius: '12px',
    },
    '& .RaFileInput-dropZone .import-info.import-info': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: 0,
        textAlign: 'center',
    },
    '& .RaFileInput-dropZone .import-info .instruction-text': {
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '-0.14px',
        margin: 0,
    },
    '& .RaFileInput-dropZone .import-info .import-preview': {
        display: 'block',
        maxWidth: '200px',
        height: 'auto',
        borderRadius: '12px',
        margin: 0,
    },
    '& .import-preview-ra': {
        marginBottom: '24px',
    },
    '& .RaFileInput-dropZone .import-info .import-preview-ra img': {
        display: 'block',
        width: '100%',
        height: 'auto',
        borderRadius: '12px',
        margin: 0,
        objectFit: 'cover',
    },
    '& .RaFileInput-dropZone .import-info .btn-import.btn-import': {
        marginBottom: '12px',
        '&:hover': {
            backgroundColor: Palette.yellow.yellow400,
            boxShadow: `inset 0 0 0 2px ${Palette.yellow.yellow600}`,
        },
    },
    '& .RaFileInput-dropZone .import-info .btn-import.loaded-margin': {
        margin: '0 auto 12px',
    },
});

const ContractorLogoInput = ({
    source,
    record,
    instructionText,
    accept,
    validate = null,
    loading = false,
    disabled = false,
    maxBytes = 150000,
    maxSizeMessage,
    existingPreview = null,
}) => {
    const translate = useTranslate();
    const placeholder = loading ? translate('resources.contractor.labels.verifyingFile') : translate('resources.contractor.labels.chooseNewLogoFile');
    const acceptedFormats = accept ?? '.jpg,.jpeg,.png,.svg';
    const notify = useNotify();

    const value = useWatch({ name: source });
    const previewUrl = useImagePreview(value);
    const { setValue } = useFormContext();
    const containerRef = useRef(null);
    const handleReupload = useCallback(
        event => {
            event.preventDefault();
            event.stopPropagation();
            if (event.nativeEvent && typeof event.nativeEvent.stopImmediatePropagation === 'function') {
                event.nativeEvent.stopImmediatePropagation();
            }
            const input = containerRef.current?.querySelector('input[type="file"]');
            if (input && !disabled) {
                input.click();
            }
        },
        [disabled],
    );

    const handleFileChange = useCallback(
        value => {
            if (!value) return;

            const file = Array.isArray(value) ? value[0] : value;
            const rawFile = file?.rawFile || file;

            if (rawFile instanceof File && rawFile.size > maxBytes) {
                const maxSizeInKB = (maxBytes / 1000).toFixed(2);
                notify(maxSizeMessage || translate('resources.contractor.validation.fileTooLargeWithMax', { maxSize: maxSizeInKB }), { type: 'error' });
                setValue(source, undefined, { shouldValidate: true });
            }
        },
        [maxBytes, maxSizeMessage, notify, setValue, source, translate],
    );

    const maxFileSize = useCallback(
        (max = maxBytes) =>
            value => {
                const sizeIsLarge = val => val?.rawFile?.size > max;
                const isTooLarge = Array.isArray(value) ? value.some(sizeIsLarge) : sizeIsLarge(value);
                if (isTooLarge) {
                    return maxSizeMessage || translate('resources.contractor.validation.fileTooLarge');
                }
                return undefined;
            },
        [maxBytes, maxSizeMessage, translate],
    );

    const combinedValidate = useCallback(
        value => {
            if (validate) {
                const externalError = validate(value);
                if (externalError) return externalError;
            }
            return maxFileSize()(value);
        },
        [maxFileSize, validate],
    );

    return (
        <StyledContractorLogoInput ref={containerRef}>
            <FileInput
                source={source}
                accept={acceptedFormats}
                validate={combinedValidate}
                disabled={disabled}
                onChange={handleFileChange}
                placeholder={
                    <Box className='import-info'>
                        {previewUrl && (
                            <ImageField
                                record={{ src: previewUrl }}
                                source='src'
                                title={translate('resources.contractor.fields.currentLogo')}
                                className='import-preview-ra'
                            />
                        )}

                        {!previewUrl && existingPreview}
                        {!previewUrl && !existingPreview && record?.currentLogo && <ContractorLogoField record={record} source='currentLogo' width={200} />}

                        <Typography
                            variant='body1'
                            className={`btn-import ${previewUrl ? 'loaded loaded-margin' : 'loaded'} ${loading ? 'loading' : ''}`}
                            role={previewUrl ? 'button' : undefined}
                            tabIndex={previewUrl ? 0 : undefined}
                            onClick={previewUrl ? handleReupload : undefined}
                            onKeyDown={
                                previewUrl
                                    ? e => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleReupload(e);
                                          }
                                      }
                                    : undefined
                            }
                            disabled={loading}
                        >
                            {loading && <ImportLoadingIcon />}
                            {!loading && <ExportIcon />}
                            {previewUrl ? translate('resources.contractor.labels.reupload') : placeholder}
                        </Typography>

                        <Typography className='instruction-text'>{instructionText}</Typography>
                    </Box>
                }
            ></FileInput>
        </StyledContractorLogoInput>
    );
};

ContractorLogoInput.propTypes = {
    source: PropTypes.string.isRequired,
    record: PropTypes.object,
    instructionText: PropTypes.string.isRequired,
    accept: PropTypes.string,
    validate: PropTypes.func,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    maxBytes: PropTypes.number,
    maxSizeMessage: PropTypes.string,
    existingPreview: PropTypes.node,
};

export default ContractorLogoInput;
